/**
 * Impact Metrics Card Component
 *
 * Shows community impact statistics fetched from /api/v1/community/{companyId}/impact
 */

import { FC } from 'react';
import {
  UsersIcon,
  BuildingLibraryIcon,
  HeartIcon,
  AcademicCapIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import type { ComponentType, SVGProps } from 'react';
import { useCommunityImpact } from '@/hooks/useCommunity';
import type { CommunityImpactMetric } from '@/types/community.types';

// ---------------------------------------------------------------------------
// Icon / colour mapping
// ---------------------------------------------------------------------------

type IconComponent = ComponentType<SVGProps<SVGSVGElement> & { className?: string }>;

interface IconStyle {
  Icon: IconComponent;
  bgClass: string;
  iconClass: string;
}

const ICON_MAP: Record<string, IconStyle> = {
  users: { Icon: UsersIcon, bgClass: 'bg-blue-100', iconClass: 'text-blue-600' },
  utensils: { Icon: HeartIcon, bgClass: 'bg-green-100', iconClass: 'text-green-600' },
  'graduation-cap': {
    Icon: AcademicCapIcon,
    bgClass: 'bg-purple-100',
    iconClass: 'text-purple-600',
  },
  'clipboard-list': {
    Icon: BuildingLibraryIcon,
    bgClass: 'bg-orange-100',
    iconClass: 'text-orange-600',
  },
};

const FALLBACK_STYLE: IconStyle = {
  Icon: SparklesIcon,
  bgClass: 'bg-gray-100',
  iconClass: 'text-gray-600',
};

function resolveIconStyle(metric: CommunityImpactMetric, index: number): IconStyle {
  const mapped = metric.icon ? ICON_MAP[metric.icon] : undefined;
  if (mapped) return mapped;
  const fallbacks = Object.values(ICON_MAP);
  return fallbacks[index % fallbacks.length] ?? FALLBACK_STYLE;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ImpactMetricsCard: FC = () => {
  const { data, isLoading, error, refetch } = useCommunityImpact();

  // Loading skeleton
  if (isLoading && !data) {
    return (
      <div className="bg-linear-to-br from-primary-50 to-blue-50 rounded-lg shadow-md p-6">
        <div className="animate-pulse">
          <div className="h-5 bg-blue-200 rounded w-1/2 mb-1"></div>
          <div className="h-4 bg-blue-100 rounded w-2/3 mb-4"></div>
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg p-4 shadow-sm">
                <div className="h-10 bg-gray-100 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-linear-to-br from-primary-50 to-blue-50 rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Community Impact</h3>
        <div className="text-center py-4">
          <p className="text-red-600 text-sm font-medium">Failed to load impact metrics</p>
          <p className="text-gray-500 text-xs mt-1">{error.message}</p>
          <button
            onClick={refetch}
            disabled={isLoading}
            className="mt-4 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? 'Retrying...' : 'Retry'}
          </button>
        </div>
      </div>
    );
  }

  const metrics = data?.metrics ?? [];

  return (
    <div className="bg-linear-to-br from-primary-50 to-blue-50 rounded-lg shadow-md p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Community Impact</h3>
        <p className="text-sm text-gray-600">Your donations are making a difference</p>
      </div>

      <div className="space-y-4">
        {metrics.map((metric, index) => {
          const { Icon, bgClass, iconClass } = resolveIconStyle(metric, index);
          const isPositive = metric.change_percent > 0;
          const changeLabelColor = isPositive
            ? 'text-green-600'
            : metric.change_percent < 0
              ? 'text-red-600'
              : 'text-blue-600';

          return (
            <div key={metric.metric_name} className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className={`p-2 ${bgClass} rounded-lg`}>
                    <Icon className={`h-6 w-6 ${iconClass}`} />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-600">{metric.metric_name}</p>
                    <p className="text-2xl font-bold text-gray-900">{metric.formatted_value}</p>
                  </div>
                </div>
                <span className={`text-xs font-medium ${changeLabelColor}`}>
                  {metric.change_label}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
